import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

export type ClinicFeatureFlagsState = {
  clinicId: string | null;
  enableTranscriptionAudioArchive: boolean;
  showAppointmentItemsOnCards: boolean;
};

const mapRowToFlags = (
  row: {
    enable_transcription_audio_archive?: boolean | null;
    show_appointment_items_on_cards?: boolean | null;
  } | null,
): Omit<ClinicFeatureFlagsState, "clinicId"> => ({
  enableTranscriptionAudioArchive: row?.enable_transcription_audio_archive === true,
  showAppointmentItemsOnCards: row?.show_appointment_items_on_cards === true,
});

/** RPC returns jsonb; normalize for mapping. */
const parseClinicFlagsRpcPayload = (data: unknown): {
  enable_transcription_audio_archive?: boolean | null;
  show_appointment_items_on_cards?: boolean | null;
} => {
  if (!data || typeof data !== "object") {
    throw new Error("invalid_rpc_response");
  }
  const o = data as Record<string, unknown>;
  return {
    enable_transcription_audio_archive: o.enable_transcription_audio_archive as boolean | null | undefined,
    show_appointment_items_on_cards: o.show_appointment_items_on_cards as boolean | null | undefined,
  };
};

export const useClinicFeatureFlags = () => {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: ["clinic-feature-flags", clinicId],
    queryFn: async (): Promise<ClinicFeatureFlagsState> => {
      if (!clinicId) {
        return {
          clinicId: null,
          enableTranscriptionAudioArchive: false,
          showAppointmentItemsOnCards: false,
        };
      }

      const { data, error } = await supabase
        .from("clinics")
        .select("id, enable_transcription_audio_archive, show_appointment_items_on_cards")
        .eq("id", clinicId)
        .maybeSingle();

      if (error) throw error;

      return {
        clinicId,
        ...mapRowToFlags(data),
      };
    },
    enabled: profile !== undefined,
  });
};

export const useUpdateClinicFeatureFlags = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: {
      enableTranscriptionAudioArchive?: boolean;
      showAppointmentItemsOnCards?: boolean;
    }): Promise<ClinicFeatureFlagsState> => {
      if (!clinicId) {
        throw new Error("clinic_required");
      }

      const hasShow = input.showAppointmentItemsOnCards !== undefined;
      const hasTranscription = input.enableTranscriptionAudioArchive !== undefined;

      // RLS on `clinics` only allows owner / manage_clinic:update; scheduling staff use RPC.
      let lastRow: {
        enable_transcription_audio_archive?: boolean | null;
        show_appointment_items_on_cards?: boolean | null;
      } | null = null;

      if (hasShow) {
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          "set_clinic_show_appointment_items_on_cards",
          {
            p_clinic_id: clinicId,
            p_enabled: input.showAppointmentItemsOnCards,
          },
        );
        if (rpcError) throw rpcError;
        lastRow = parseClinicFlagsRpcPayload(rpcData);
      }

      if (hasTranscription) {
        const { data, error } = await supabase
          .from("clinics")
          .update({
            enable_transcription_audio_archive: input.enableTranscriptionAudioArchive,
          })
          .eq("id", clinicId)
          .select("id, enable_transcription_audio_archive, show_appointment_items_on_cards")
          .maybeSingle();

        if (error) throw error;
        if (!data) {
          throw new Error("clinic_update_no_row");
        }
        lastRow = data;
      }

      if (!lastRow) {
        throw new Error("no_fields");
      }

      return {
        clinicId,
        ...mapRowToFlags(lastRow),
      };
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["clinic-feature-flags", result.clinicId], result);
      void queryClient.invalidateQueries({ queryKey: ["clinic-feature-flags", result.clinicId] });
    },
  });
};
